import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { FaPlus } from 'react-icons/fa6';
import { MdDelete } from 'react-icons/md';
import { IoChevronDownOutline } from 'react-icons/io5';
import { useFieldArray, useWatch } from 'react-hook-form';
import Button from '@/components/Common/Button';
import Select from '@/components/Common/Select';
import FormSchemaParser from '@/components/Common/FormSchemaParser';

interface OpenDentalFormProps {
  namePrefix: string;
  control: any;
  disabled?: boolean;
  integrationId: string;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

export default function OpenDentalForm({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm
}: OpenDentalFormProps) {
  const [expanded, setExpanded] = useState(false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${namePrefix}.configuration`
  });

  const formFields = configFields?.filter((field: any) => field.key !== 'configuration');
  const providersField = configFields?.find((field: any) => field.key === 'configuration');
  const values = useWatch({ control, name: `${namePrefix}.configuration` }) || [];

  useEffect(() => {
    if (values?.length > 0) setExpanded(true);
  }, [values.length]);

  return (
    <>
      <FormSchemaParser
        disabled={disabled}
        fields={formFields || []}
        control={control}
        namePrefix='configuration'
        gridSize={12}
        spacing={spacing}
        submitForm={(val: any) => submitForm(val)}
      />

      {providersField?.group?.[0]?.options?.length > 0 && (
        <Accordion
          expanded={expanded}
          onChange={(event, isExpanded) => setExpanded(isExpanded)}
          disabled={disabled}
          sx={{
            boxShadow: 'none',
            border: '1px solid #F3F3F3',
            borderRadius: 2,
            mt: 2,
            '&::before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
            aria-controls='curve-dental-config-content'
            id='curve-dental-config-header'
          >
            <Typography variant='h6' fontSize={16} fontWeight={600}>
              {providersField?.label}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            {fields.map((field, index) => (
              <ProviderOperatoryRow
                key={field.id}
                providerOptions={providersField?.group?.[0]?.options}
                index={index}
                namePrefix={namePrefix}
                control={control}
                remove={remove}
                disabled={disabled}
              />
            ))}

            {!disabled && (
              <Button
                type='button'
                color='secondary'
                size='small'
                variant='outlined'
                startIcon={<FaPlus />}
                onClick={() => append({ provider: '', operatory: '' })}
                sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
              >
                Add Configuration
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}

interface ProviderOperatoryRowProps {
  index: number;
  namePrefix: string;
  control: any;
  remove: (index: number) => void;
  disabled?: boolean;
  providerOptions: any[];
}

const ProviderOperatoryRow = ({
  index,
  namePrefix,
  control,
  remove,
  disabled,
  providerOptions
}: ProviderOperatoryRowProps) => {
  // Watch the entire configuration array to manage unique selections
  const configurationValues =
    useWatch({
      control,
      name: `${namePrefix}.configuration`
    }) || [];

  const selectedProviders = configurationValues
    .map((conf: any, i: number) => (i === index ? null : conf?.provider))
    .filter(Boolean);

  const filteredProviderOptions = providerOptions?.filter((opt: any) => !selectedProviders.includes(opt.value)) || [];

  return (
    <Box display='flex' alignItems='center' gap={2} mb={2}>
      <Select
        name={`${namePrefix}.configuration[${index}].provider`}
        control={control}
        label='Provider'
        disabled={disabled}
        required
        options={filteredProviderOptions}
      />

      {!disabled && (
        <IconButton size='small' color='error' onClick={() => remove(index)}>
          <MdDelete />
        </IconButton>
      )}
    </Box>
  );
};
